import { useEffect, useMemo } from 'react';

import { meshModules } from 'Deer/constants/modules';
import { L3_PATHS } from 'Deer/constants/routes';
import { useRouteToMyTeamLevels, useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { IntentFilters } from 'Deer/intent/components/Layout/IntentFilters.layout';
import { IntentBreadcrumbType } from 'Deer/intent/types/IntentCommonTypes';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { checkCompensationModuleEnabled } from 'utils/helpers/moduleAccess';

import { COMPENSATION_INTENTS, FEEDBACK_INTENTS } from 'constants/moduleIntentTypes';

import { AgGridEllipsisMenu } from 'uiToolkitV1/AgGridWrapperV2/components/EllipsisMenu/AgGridEllipsisMenu';

import { CompensationIntentType, setSelectedCompensationIntent } from '../slice/CompensationIntentSlice';
import { CompensationMetaFilters } from './CompensationMetaFilters';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CompensationIntentDropdown = ({
  showKebabMenu = true,
  breadcrumbs,
}: {
  showKebabMenu?: boolean;
  breadcrumbs: IntentBreadcrumbType;
}) => {
  const { l1Module } = useRouteToNavLevels();
  const { teamType } = useRouteToMyTeamLevels();
  const history = useHistory();
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  // eslint-disable-next-line arrow-body-style
  const ellipsisMenuOptions = useMemo(() => {
    const options = [];

    return options;
  }, []);

  // eslint-disable-next-line arrow-body-style
  const intentDropdownOptions = useMemo<Array<CompensationIntentType>>(() => {
    return [
      {
        id: COMPENSATION_INTENTS.COMPENSATION_PLANNING,
        label: `Planning`,
        onClick: () => {
          const values = {
            team: {
              directReports: L3_PATHS.team.directReports.compensation.planning,
              allReports: L3_PATHS.team.allReports.compensation.planning,
            },
            company: L3_PATHS.company.compensation.planning,
          };
          const isTeamRoute = l1Module === 'team';
          const urlToPush = isTeamRoute ? values.team[teamType] : (values[l1Module] ?? values.company);

          history.push({
            pathname: urlToPush,
          });
        },
        visible: () => l1Module !== 'myData',
        value: {
          team: {
            directReports: {
              path: L3_PATHS.team.directReports.compensation.planning,
              isAccessible: checkCompensationModuleEnabled,
            },
            allReports: {
              path: L3_PATHS.team.allReports.compensation.planning,
              isAccessible: checkCompensationModuleEnabled,
            },
          },
          company: {
            path: L3_PATHS.company.compensation.planning,
            isAccessible: checkCompensationModuleEnabled,
          },
        },
        showTimelineDropdown: false,
        category: 'Manage',
        isDefault: true,
      },
      {
        id: COMPENSATION_INTENTS.COMPENSATION_SETTINGS,
        label: `Settings`,
        onClick: () => {
          const values = {
            company: L3_PATHS.company.compensation.settings,
          };
          const urlToPush = values[l1Module] ?? values.company;

          history.push({
            pathname: urlToPush,
          });
        },
        visible: () => l1Module === 'company',
        value: {
          team: {},
          company: {
            path: L3_PATHS.company.compensation.settings,
            isAccessible: checkCompensationModuleEnabled,
          },
        },
        showTimelineDropdown: false,
        category: 'Manage',
        isDefault: true,
      },
    ];
  }, [l1Module, teamType, history]);

  const selectedIntent = useMemo(
    () =>
      intentDropdownOptions.find((option) => {
        const isTeamType = l1Module === 'team';
        if (isTeamType) {
          return pathname.includes(option.value.team?.[teamType]?.path);
        }
        return pathname.includes(option.value[l1Module]?.path);
      }),
    [intentDropdownOptions, l1Module, pathname, teamType]
  );

  useEffect(() => {
    // if selectedIntent changes then update it in redux
    dispatch(setSelectedCompensationIntent(selectedIntent));
  }, [selectedIntent, dispatch]);

  const ellipsisMenuOptionsExist = ellipsisMenuOptions?.length > 0;

  return (
    <IntentFilters
      intentDropdownOptions={intentDropdownOptions.filter((option) => option.visible())}
      selectedIntent={selectedIntent?.label ?? ''}
      selectedIntentId={selectedIntent?.id ?? ''}
      KebabMenu={ellipsisMenuOptionsExist && <AgGridEllipsisMenu options={ellipsisMenuOptions} />}
      MetaFilters={
        selectedIntent?.id === COMPENSATION_INTENTS.COMPENSATION_PLANNING ? <CompensationMetaFilters /> : null
      }
      showKebabMenu={
        l1Module === 'team' && selectedIntent?.id !== FEEDBACK_INTENTS.TEAM_FEEDBACK ? false : showKebabMenu
      }
      moduleName={meshModules.compensation.value}
      intentTitle={meshModules.compensation.label}
      isCategorized
      breadcrumbs={breadcrumbs}
    />
  );
};

export { CompensationIntentDropdown };
